import { useState } from 'react';
import type { Page } from '@connaro/shared';
import { ApiError, api } from '../api/client';
import { Banner, EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Site {
  id: string;
  name: string;
}
interface Token {
  id: string;
  label: string;
  prefix: string;
  maxUses: number;
  useCount: number;
  autoApprove: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
}

export function EnrollmentTokensPage(): JSX.Element {
  const t = useT();
  const sites = useAsync(() => api.get<Page<Site>>('/sites?limit=100'), []);
  const tokens = useAsync(() => api.get<Token[]>('/enrollment-tokens'), []);
  const [label, setLabel] = useState('');
  const [siteId, setSiteId] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      const res = await api.post<{ token: string }>('/enrollment-tokens', {
        label,
        siteId,
        autoApprove,
      });
      setIssued(res.token);
      setLabel('');
      tokens.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const onRevoke = async (id: string): Promise<void> => {
    await api.del(`/enrollment-tokens/${id}`);
    tokens.reload();
  };

  const siteOptions = sites.data?.items ?? [];

  return (
    <section>
      <div className="page-head">
        <h1>{t('enrollment.title')}</h1>
      </div>

      <div className="card">
        {issued ? (
          <Banner variant="warning">
            {t('enrollment.tokenOnce')} <code>{issued}</code>
          </Banner>
        ) : null}
        {error ? <Banner variant="error">{error}</Banner> : null}
        <form className="form form--inline" onSubmit={(e) => void onCreate(e)}>
          <label className="field">
            <span>{t('enrollment.label')}</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} required />
          </label>
          <label className="field">
            <span>{t('nav.sites')}</span>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
              <option value="">—</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('enrollment.autoApprove')}</span>
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={(e) => setAutoApprove(e.target.checked)}
            />
          </label>
          <button type="submit" className="btn">
            {t('enrollment.create')}
          </button>
        </form>
      </div>

      {tokens.loading ? (
        <Loading />
      ) : tokens.error ? (
        <ErrorState message={tokens.error.message} onRetry={tokens.reload} />
      ) : !tokens.data || tokens.data.length === 0 ? (
        <EmptyState hint={t('enrollment.empty')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('enrollment.label')}</th>
                <th>Prefix</th>
                <th>{t('enrollment.uses')}</th>
                <th>{t('enrollment.autoApprove')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {tokens.data.map((tok) => (
                <tr key={tok.id}>
                  <td>{tok.label}</td>
                  <td>
                    <code>{tok.prefix}…</code>
                  </td>
                  <td>
                    {tok.useCount}/{tok.maxUses}
                  </td>
                  <td>{tok.autoApprove ? '✓' : '—'}</td>
                  <td className="row-actions">
                    {tok.revokedAt ? (
                      <span className="chip">revoked</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--ghost btn--danger"
                        onClick={() => void onRevoke(tok.id)}
                      >
                        {t('enrollment.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
